import React, { FC } from 'react';
import { Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, InputLabel, MenuItem, Select } from '@mui/material';
import { IDropdownSelectOption } from '../../../DropdownSearchSelect/DropdownSearchSelect';
import TextInput from '../../../TextInput/TextInput';
import TextFieldInput from '../../../TextFieldInput/TextFieldInput';
import { ICampaignFormData, IHTMLPopUpTemplateFormData } from '../types';
import { alertTypeOptions, insertionTypeOptions } from '../constants';
import HTMLPopUpTemplateFields from './HTMLPopUpTemplateFields/HTMLPopUpTemplateFields';
import HTMLPreview from '../HTMLPreview/HTMLPreview';

interface ICampaignDisplayTypeFields {
  displayType: IDropdownSelectOption | null;
  formData: ICampaignFormData;
  setFormData: (data: (prev: ICampaignFormData) => ICampaignFormData) => void;
  setFormErrors: (errors: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  setHTMLPopUpTemplateFormData: (data: (prev: IHTMLPopUpTemplateFormData) => IHTMLPopUpTemplateFormData) => void;
  htmlPopUpTemplateFormData: IHTMLPopUpTemplateFormData;
  formErrors: Record<string, boolean>;
  isEditingDisabled?: boolean;
}

/**
 * Props interface for the CampaignDisplayTypeFields component
 * @interface ICampaignDisplayTypeFields
 * @property {IDropdownSelectOption | null} displayType - The selected display type option
 * @property {ICampaignFormData} formData - The form data object containing campaign fields
 * @property {function} setFormData - Function to update form data, takes a function that receives previous state
 * @property {function} setFormErrors - Function to update form errors, takes a function that receives previous state
 * @property {Record<string, boolean>} formErrors - Object containing form validation errors
 * @property {boolean} [isEditingDisabled] - Optional flag to disable form editing
 */

const CampaignDisplayTypeFields: FC<ICampaignDisplayTypeFields> = ({
  displayType,
  formData,
  setFormData,
  setFormErrors,
  formErrors,
  setHTMLPopUpTemplateFormData,
  htmlPopUpTemplateFormData,
  isEditingDisabled = false,
}) => {
  if (!displayType) return null;

  const handleInputChange = (value: string, field: keyof ICampaignFormData) => {
    if (value) {
      setFormErrors((prev: Record<string, boolean>) => ({ ...prev, [field]: false }));
    }
    setFormData((prev: ICampaignFormData) => ({ ...prev, [field]: value }));
  };

  const renderFields = () => {
    switch (displayType.value) {
      case 'POP_UP':
        return (
          <>
            <div className="flex flex-col flex-1 mb-2">
              <TextInput
                label="Headline"
                value={formData.headline}
                hasError={formErrors.headline}
                placeholderText="Enter Headline"
                handleChange={(value: string) => {
                  handleInputChange(value, 'headline');
                }}
                errorText={'Headline is required'}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextFieldInput
                label="Main Text"
                value={formData.mainText}
                handleChange={(value: string) => {
                  handleInputChange(value, 'mainText');
                }}
                hasError={formErrors.mainText}
                errorText={'Main Text is required'}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-row gap-4">
              <div className="flex flex-col flex-1">
                <TextInput
                  label="CTA Color (Hex)"
                  value={formData.ctaColor}
                  placeholderText="Enter CTA Color (Hex)"
                  handleChange={(value: string) => {
                    handleInputChange(value, 'ctaColor');
                  }}
                  hasError={formErrors.ctaColor}
                  errorText={'CTA Color is required'}
                  isDisabled={isEditingDisabled}
                />
              </div>
              <div className="flex flex-col flex-1">
                <TextInput
                  label="CTA Label"
                  value={formData.ctaLabel}
                  placeholderText="Enter CTA Label"
                  handleChange={(value: string) => {
                    handleInputChange(value, 'ctaLabel');
                  }}
                  hasError={formErrors.ctaLabel}
                  errorText={'CTA Label is required'}
                  isDisabled={isEditingDisabled}
                />
              </div>
            </div>
          </>
        );
      case 'INLINE':
      case 'HTML_POP_UP':
        return (
          <>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Target"
                value={formData.target}
                placeholderText="Enter Target"
                handleChange={(value: string) => {
                  handleInputChange(value, 'target');
                }}
                hasError={formErrors.target}
                errorText={'Target is required'}
                isDisabled={isEditingDisabled}
              />
            </div>
            {displayType.value === 'INLINE' && (
              <div className="flex flex-col flex-1">
                <FormControl fullWidth size="small" error={formErrors.insertion} disabled={isEditingDisabled}>
                  <InputLabel id="insertion-type-label">Insertion</InputLabel>
                  <Select
                    data-testid="insertion-select"
                    labelId="insertion-type-label"
                    value={formData.insertion?.value ?? ''}
                    label="Insertion"
                    onChange={e => {
                      const value = e.target.value as string;
                      const option = insertionTypeOptions.find(o => o.value === value) ?? null;
                      if (option) {
                        setFormErrors((prev: Record<string, boolean>) => ({ ...prev, insertion: false }));
                      }
                      setFormData((prev: ICampaignFormData) => ({ ...prev, insertion: option }));
                    }}
                  >
                    {insertionTypeOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.insertion && (
                    <FormHelperText>Insertion is required</FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
            <div className="flex flex-col">
              <TextFieldInput
                label="HTML Content"
                value={formData.content}
                handleChange={(value: string) => {
                  handleInputChange(value, 'content');
                }}
                hasError={formErrors.content}
                errorText={'Content is required'}
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col">
              <TextFieldInput
                label="HTML Style"
                value={formData.style}
                handleChange={(value: string) => {
                  handleInputChange(value, 'style');
                }}
                hasError={formErrors.style}
                errorText="Style is required"
                isDisabled={isEditingDisabled}
              />
            </div>
            <HTMLPreview htmlContent={formData.content} htmlStyle={formData.style} label="Live Preview" />
          </>
        );
      case 'HTML_POP_UP_TEMPLATE':
        return (
          <HTMLPopUpTemplateFields
            htmlPopUpTemplateFormData={htmlPopUpTemplateFormData}
            setHTMLPopUpTemplateFormData={setHTMLPopUpTemplateFormData}
            isEditingDisabled={isEditingDisabled}
          />
        );
      case 'ALERT_HTML':
        return (
          <>
            <div className="flex flex-col flex-1 mb-2">
              <FormControl component="fieldset" error={formErrors.alertTypes} disabled={isEditingDisabled}>
                <FormLabel component="legend" className="mb-1">Alert Types</FormLabel>
                <FormGroup>
                  {alertTypeOptions.map(option => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={formData.alertTypes.includes(option.value)}
                          onChange={e => {
                            const next = e.target.checked
                              ? [...formData.alertTypes, option.value]
                              : formData.alertTypes.filter(t => t !== option.value);
                            if (next.length > 0) {
                              setFormErrors((prev: Record<string, boolean>) => ({ ...prev, alertTypes: false }));
                            }
                            setFormData((prev: ICampaignFormData) => ({ ...prev, alertTypes: next }));
                          }}
                        />
                      }
                      label={option.label}
                    />
                  ))}
                </FormGroup>
                {formErrors.alertTypes && <FormHelperText>At least one alert type is required</FormHelperText>}
              </FormControl>
            </div>
            <div className="flex flex-col">
              <TextFieldInput
                label="HTML Content"
                value={formData.content}
                handleChange={(value: string) => handleInputChange(value, 'content')}
                hasError={formErrors.content}
                errorText="Content is required"
                isDisabled={isEditingDisabled}
              />
            </div>
            <div className="flex flex-col">
              <TextFieldInput
                label="HTML Style"
                value={formData.style}
                handleChange={(value: string) => handleInputChange(value, 'style')}
                hasError={formErrors.style}
                errorText="Style is required"
                isDisabled={isEditingDisabled}
              />
            </div>
            <HTMLPreview htmlContent={formData.content} htmlStyle={formData.style} label="Live Preview" />
          </>
        );
    }
  };

  return (
    <div>
      <h3 className="text-muted-dark-purple">{displayType.label} Campaign Fields</h3>
      {renderFields()}
    </div>
  );
};

export default CampaignDisplayTypeFields;
